import type { FC } from 'react';
import { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { ContainerGrid, ContainerGridItem } from '@makemydeal/dr-dash-components';
import { Box } from '@interstate/components/Box';
import { TextInput } from '@interstate/components/TextInput';
import { SelectInput } from '@interstate/components/SelectInput';
import { PostalCodeInput } from '@interstate/components/PostalCodeInput';
import { locationCodeList } from '../vehicleProtection/constants';
import type { DraftDealProduct } from '@makemydeal/dr-dash-types';
import { getIsContractingContext } from '@makemydeal/dr-dash-store/selectors/contractingSelectors';
import type { ValidationEngine } from '@makemydeal/dr-shared-ui-utils/validationEngine/ValidationEngine';

export interface ProviderAddressDetailsProps {
    validator: ValidationEngine;
    formErrors: any;
    providerAddress: DraftDealProduct['productProviderAddress'];
    onProviderAddressChange: (address: DraftDealProduct['productProviderAddress']) => void;
    onValidationError: (hasError: boolean) => void;
}

const PROVIDER_ADDRESS_FIELD_MAP: Record<string, string> = {
    streetAddress: 'addressLine1',
    city: 'city',
    state: 'stateProvince',
    zip: 'postalCode'
};

const validationIconStyles = {
    '.interstate-exclamation-circle-icon': { flexShrink: 0 }
};

const combineProviderAddressLines = (address: DraftDealProduct['productProviderAddress']): string => {
    // NOTE: it has 3 address lines, they can be nullish, so we need to prevent showing 'undefined' or 'null'
    return [address?.addressLine1, address?.addressLine2, address?.addressLine3].filter(Boolean).join(', ');
};

const resetProviderAddressUnsupportedFields = (
    address: DraftDealProduct['productProviderAddress']
): DraftDealProduct['productProviderAddress'] => {
    return {
        ...address,
        addressLine1: combineProviderAddressLines(address),
        addressLine2: null,
        addressLine3: null,
        addressType: 'Other',
        countyDistrict: null,
        country: 'US'
    };
};

export const ProviderAddressDetails: FC<ProviderAddressDetailsProps> = ({
    validator,
    formErrors,
    providerAddress,
    onProviderAddressChange,
    onValidationError
}) => {
    const [address, setAddress] = useState<DraftDealProduct['productProviderAddress']>(
        providerAddress || {
            addressType: 'Other',
            country: 'US'
        }
    );
    const [localZip, setLocalZip] = useState<string>(providerAddress?.postalCode || '');
    const isContractingContext = useSelector(getIsContractingContext);

    // Local copy of error state used for JSX display.
    // Updated after each targeted validatePath call (refresh: false avoids validateAll,
    // which would incorrectly re-evaluate the shared `terms` field).
    const [displayFormErrors, setDisplayFormErrors] = useState(() =>
        validator.getFormErrorState({ channel: 'inline', refresh: false })
    );

    useEffect(() => {
        validator.ctx.entity = 'provider';
    }, [validator]);

    // Helper: reads current error presence from the errorStore (no validateAll).
    const notifyValidationError = useCallback(() => {
        onValidationError(validator.getHasFormErrors({ refresh: false }));
    }, [validator, onValidationError]);

    useEffect(() => {
        // Secondary trigger: the parent's wrapped engine calls syncFormErrors when any field is
        // validated, causing `formErrors` to update and this effect to re-run. This keeps
        // onValidationError in sync in production even if the local notifyValidationError call
        // is missed (e.g. external address changes).
        notifyValidationError();
    }, [formErrors, notifyValidationError]);

    useEffect(() => {
        setAddress(providerAddress);
        // Use setValue to keep the wrapped engine's formErrors in sync and
        // avoid direct ctx.values mutation. validate: true re-validates only
        // that field — not unrelated fields like `terms`.
        validator.setValue('streetAddress', combineProviderAddressLines(providerAddress), { validate: true });
        validator.setValue('city', providerAddress?.city || '', { validate: true });
        validator.setValue('state', providerAddress?.stateProvince || '', { validate: true });
        validator.setValue('zip', providerAddress?.postalCode || '', { validate: true });
        const next = validator.getFormErrorState({ channel: 'inline', refresh: false });
        setDisplayFormErrors(next);
        onValidationError(validator.getHasFormErrors({ refresh: false }));
        // Sync localZip only if it's different from what we have (external change)
        const newZip = providerAddress?.postalCode || '';
        if (newZip !== localZip) {
            setLocalZip(newZip);
        }
    }, [providerAddress, validator]);

    useEffect(() => {
        // Re-validate only address fields when contracting context changes so
        // contract-flow rules are re-applied without touching unrelated fields.
        validator.validatePath('streetAddress', validator.ctx.values.streetAddress as string);
        validator.validatePath('city', validator.ctx.values.city as string);
        validator.validatePath('state', validator.ctx.values.state as string);
        validator.validatePath('zip', validator.ctx.values.zip as string);
        const next = validator.getFormErrorState({ channel: 'inline', refresh: false });
        setDisplayFormErrors(next);
        onValidationError(validator.getHasFormErrors({ refresh: false }));
    }, [validator, isContractingContext]);

    const updateAddressAndNotify = useCallback(
        (newAddress: DraftDealProduct['productProviderAddress']) => {
            onProviderAddressChange(resetProviderAddressUnsupportedFields(newAddress));
        },
        [onProviderAddressChange]
    );

    const onFieldChange = useCallback(
        (event: any) => {
            const { name: field, value: rawValue } = event?.target ?? {};
            if (!field) return;

            const value = String(rawValue ?? '').trim();

            if (field === 'zip') {
                setLocalZip(value);
            }

            validator.setValue(field, value, { validate: true });
            setDisplayFormErrors(validator.getFormErrorState({ channel: 'inline', refresh: false }));
            notifyValidationError();

            const addressKey = PROVIDER_ADDRESS_FIELD_MAP[field] ?? field;
            const currentValue =
                field === 'streetAddress' ? combineProviderAddressLines(address) : String((address as any)?.[addressKey] ?? '');

            if (value === currentValue) return;

            updateAddressAndNotify({ ...(address as any), [addressKey]: value } as any);
        },
        [address, updateAddressAndNotify, validator]
    );

    return (
        <Box data-testid="edit-product-drawer-vpp-provider-address-container" mb={2}>
            <Box mb={2}>
                <TextInput
                    label="Address"
                    name="streetAddress"
                    data-testid="edit-product-drawer-vpp-provider-address"
                    value={combineProviderAddressLines(address)}
                    onChange={onFieldChange}
                    sx={validationIconStyles}
                    placeholder="Enter address"
                    hasError={displayFormErrors.streetAddress.hasError || !!formErrors?.apiStreetAddress?.hasError}
                    errorMessage={displayFormErrors.streetAddress.errorMessage || formErrors?.apiStreetAddress?.errorMessage}
                />
            </Box>
            <ContainerGrid $alignItems="flex-start">
                <ContainerGridItem md={4}>
                    <TextInput
                        label="City"
                        name="city"
                        data-testid="edit-product-drawer-vpp-provider-address-city"
                        value={address?.city || ''}
                        sx={validationIconStyles}
                        onChange={onFieldChange}
                        placeholder="Enter city"
                        hasError={displayFormErrors.city.hasError || !!formErrors?.apiCity?.hasError}
                        errorMessage={displayFormErrors.city.errorMessage || formErrors?.apiCity?.errorMessage}
                    />
                </ContainerGridItem>
                <ContainerGridItem md={4}>
                    <SelectInput
                        data-testid="edit-product-drawer-vpp-provider-address-state"
                        label="State"
                        name="state"
                        value={address?.stateProvince || ''}
                        onChange={onFieldChange}
                        options={locationCodeList.map((state) => ({
                            value: state,
                            label: state
                        }))}
                        hasError={displayFormErrors.state.hasError || !!formErrors?.apiState?.hasError}
                        errorMessage={displayFormErrors.state.errorMessage || formErrors?.apiState?.errorMessage}
                        displayDeselectOption={false}
                    />
                </ContainerGridItem>
                <ContainerGridItem md={4}>
                    <PostalCodeInput
                        label="ZIP"
                        name="zip"
                        data-testid="edit-product-drawer-vpp-provider-address-zip"
                        value={localZip}
                        onChange={onFieldChange}
                        placeholder="Enter zip"
                        errorMessage={displayFormErrors.zip.errorMessage || formErrors?.apiZip?.errorMessage}
                        hasError={displayFormErrors.zip.hasError || !!formErrors?.apiZip?.hasError}
                        country="US"
                        sx={validationIconStyles}
                    />
                </ContainerGridItem>
            </ContainerGrid>
        </Box>
    );
};
